const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || '';

/**
 * Returns the organization subdomain extracted from the current URL, or null
 * if the app is running on the root domain / plain localhost.
 *
 * Environment   URL example                  Subdomain returned
 * -----------   ---------------------------  ------------------
 * Dev           tekhub.localhost:3001        "tekhub"
 * Dev           localhost:3001               null
 * Production    mover.teckhub.site           "mover"
 * Production    teckhub.site                 null
 */
export function getSubdomain() {
  const hostname = window.location.hostname;

  // Plain localhost or IP address — no subdomain
  if (hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return null;
  }

  // Dev: *.localhost  →  strip ".localhost" suffix
  // tekhub.localhost → "tekhub"
  if (hostname.endsWith('.localhost')) {
    const sub = hostname.slice(0, hostname.lastIndexOf('.localhost'));
    return sub || null;
  }

  // Production: mover.teckhub.site → strip BASE_DOMAIN → "mover"
  if (BASE_DOMAIN && hostname.endsWith(`.${BASE_DOMAIN}`)) {
    const sub = hostname.slice(0, hostname.length - BASE_DOMAIN.length - 1);
    return sub || null;
  }

  const parts = hostname.split('.');

  // Root domain (e.g. teckhub.site) — no org subdomain
  if (parts.length === 2) {
    return null;
  }

  // Fallback for unknown domains: org subdomain is always parts[0]
  if (parts.length >= 3) {
    return parts[0];
  }

  return null;
}

/**
 * Builds the correct workspace URL for the given subdomain based on the
 * current environment, so the user can be redirected to the right place.
 *
 * Dev:  buildWorkspaceUrl("mover") → http://mover.localhost:3001
 * Prod: buildWorkspaceUrl("mover") → https://mover.teckhub.site
 */
export function buildWorkspaceUrl(subdomain) {
  const { protocol, hostname, port } = window.location;
  const portStr = port ? `:${port}` : '';

  // Dev: plain localhost → prepend subdomain
  if (hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return `${protocol}//${subdomain}.localhost${portStr}`;
  }

  // Production: build subdomain.teckhub.site
  if (BASE_DOMAIN) {
    return `${protocol}//${subdomain}.${BASE_DOMAIN}${portStr}`;
  }

  // Fallback: replace parts[0] with subdomain
  const parts = hostname.split('.');
  parts[0] = subdomain;
  return `${protocol}//${parts.join('.')}${portStr}`;
}
